import { Dispatch, SetStateAction, useCallback } from 'react'
import { BasketType } from 'types/basket'
import { UserType } from 'types/user'
import { parseHirakanaToKanakana } from 'lib/helper'
import { StoreType } from 'types/store'
import { OrderType } from 'types/checkout'
import { db } from 'firebase/index'
import { mergeCheckoutWithItems, mergeCheckoutItems } from 'reducks/checkout/operations'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { useDispatch } from 'react-redux'
import { StoreShippingType } from '../../types/storeShipping'

const isMatchedStoreName = (keyword: string, store: StoreType | undefined): boolean => {
  if (!store) return false
  const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
  const hirakanaRegexp = new RegExp('^(?=.*' + parseHirakanaToKanakana(keyword) + ').*$', 'g')
  const upperRegexp = new RegExp('^(?=.*' + keyword.toUpperCase() + ').*$', 'g')
  const lowerRegexp = new RegExp('^(?=.*' + keyword.toLowerCase() + ').*$', 'g')
  return (
    regexp.test(store.name) ||
    regexp.test(store.name_kana) ||
    hirakanaRegexp.test(store.name_kana) ||
    upperRegexp.test(store.name.toUpperCase()) ||
    upperRegexp.test(store.name_kana.toUpperCase()) ||
    lowerRegexp.test(store.name.toLowerCase()) ||
    lowerRegexp.test(store.name_kana.toLowerCase())
  )
}

const fetchBasketsByStores = async (stores: StoreType[]): Promise<BasketType[]> => {
  const promises = []
  for (let i = 0; i < stores.length; i = (i + 1) | 0) {
    const store = stores[i]
    promises.push(
      db
        .collectionGroup('store_baskets')
        .where('store_ref', '==', db.collection('stores').doc(store.store_id))
        .get()
        .then((snapshots) => {
          const baskets: BasketType[] = []
          snapshots.forEach((snapshot) => {
            const data = snapshot.data() as BasketType
            baskets.push(data)
          })
          return baskets
        })
    )
  }
  const basketsList: BasketType[][] = await Promise.all(promises)
  return basketsList.flat()
}

const fetchBasketsByUsers = async (users: UserType[]): Promise<BasketType[]> => {
  const promises = []
  for (let i = 0; i < users.length; i = (i + 1) | 0) {
    const user = users[i]
    promises.push(
      db
        .collectionGroup('store_baskets')
        .where('user_id', '==', user.user_id)
        .get()
        .then((snapshots) => {
          const baskets: BasketType[] = []
          snapshots.forEach((snapshot) => {
            const data = snapshot.data() as BasketType
            baskets.push(data)
          })
          return baskets
        })
    )
  }
  const basketsList: BasketType[][] = await Promise.all(promises)
  return basketsList.flat()
}

const fetchStoreShippingsByStores = async (stores: StoreType[]): Promise<StoreShippingType[]> => {
  const promises = []
  for (let i = 0; i < stores.length; i = (i + 1) | 0) {
    const store = stores[i]
    promises.push(
      db
        .collectionGroup('store_shippings')
        .where('store_ref', '==', db.collection('stores').doc(store.store_id))
        .get()
        .then((snapshots) => {
          const storeShippings: StoreShippingType[] = []
          snapshots.forEach((snapshot) => {
            const data = snapshot.data() as StoreShippingType
            storeShippings.push(data)
          })
          return storeShippings
        })
    )
  }
  const storeShippingsList: StoreShippingType[][] = await Promise.all(promises)
  return storeShippingsList.flat()
}

const fetchStoreShippingsByUsers = async (users: UserType[]): Promise<StoreShippingType[]> => {
  const promises = []
  for (let i = 0; i < users.length; i = (i + 1) | 0) {
    const user = users[i]
    promises.push(
      db
        .collectionGroup('store_shippings')
        .where('user_id', '==', user.user_id)
        .get()
        .then((snapshots) => {
          const baskets: StoreShippingType[] = []
          snapshots.forEach((snapshot) => {
            const data = snapshot.data() as StoreShippingType
            baskets.push(data)
          })
          return baskets
        })
    )
  }
  const storeShippingsList: StoreShippingType[][] = await Promise.all(promises)
  return storeShippingsList.flat()
}

const searchBaskets = async (regexp: RegExp): Promise<BasketType[]> => {
  return db
    .collectionGroup('store_baskets')
    .get()
    .then((snapshots) => {
      const baskets: BasketType[] = []
      snapshots.forEach((snapshot) => {
        // 100件まで取得
        if (baskets.length >= 100) return
        const data = snapshot.data() as BasketType
        if (!regexp.test(data.basket_number)) return
        baskets.push(data)
      })
      return baskets
    })
    .catch((error) => {
      console.error(error)
      return []
    })
}

const searchStoreShippings = async (regexp: RegExp): Promise<StoreShippingType[]> => {
  return db
    .collectionGroup('store_shippings')
    .get()
    .then((snapshots) => {
      const storeShippings: StoreShippingType[] = []
      snapshots.forEach((snapshot) => {
        // 100件まで取得
        if (storeShippings.length >= 100) return
        const data = snapshot.data() as StoreShippingType
        if (!regexp.test(data.shipping_number)) return
        storeShippings.push(data)
      })
      return storeShippings
    })
    .catch((error) => {
      console.error(error)
      return []
    })
}

const searchUsers = async (regexp: RegExp): Promise<UserType[]> => {
  return db
    .collection('users')
    .get()
    .then((snapshots) => {
      const users: UserType[] = []
      snapshots.forEach((snapshot) => {
        const data = snapshot.data() as UserType
        if (!regexp.test(data.name)) return
        data.user_id = snapshot.id
        users.push(data)
      })
      return users
    })
    .catch((error) => {
      console.error(error)
      return []
    })
}

const mergeOrders = async (date: string, userId: string): Promise<OrderType[]> => {
  const snapshots = await db
    .collectionGroup('store_checkouts')
    .where('date', '==', date)
    .where('user_id', '==', userId)
    .get()
  const items = await mergeCheckoutItems(snapshots, [])
  return mergeCheckoutWithItems(snapshots, items)
}

const fetchOrdersByBaskets = async (baskets: BasketType[]): Promise<OrderType[]> => {
  // store_checkoutsを取得
  const promises = []
  for (let i = 0; i < baskets.length; i = (i + 1) | 0) {
    const basket = baskets[i]
    promises.push(mergeOrders(basket.date, basket.user_id))
  }
  const ordersList: OrderType[][] = await Promise.all(promises)
  const orders = ordersList.flat()
  orders.sort((a, b) => {
    if (!a.checkout_at || !b.checkout_at) {
      return a.updated_at > b.updated_at ? -1 : 1
    } else {
      return a.checkout_at > b.checkout_at ? -1 : 1
    }
  })
  return orders
}

const fetchOrdersByStoreShippings = async (storeShippings: StoreShippingType[]): Promise<OrderType[]> => {
  // store_checkoutsを取得
  const promises = []
  for (let i = 0; i < storeShippings.length; i = (i + 1) | 0) {
    const storeShipping = storeShippings[i]
    promises.push(mergeOrders(storeShipping.scheduled_delivery_date, storeShipping.user_id))
  }
  const ordersList: OrderType[][] = await Promise.all(promises)
  const orders = ordersList.flat()
  orders.sort((a, b) => {
    if (!a.checkout_at || !b.checkout_at) {
      return a.updated_at > b.updated_at ? -1 : 1
    } else {
      return a.checkout_at > b.checkout_at ? -1 : 1
    }
  })
  return orders
}

// フォームに入力された値を部分一致で検索
const useSearchOrder = (params: {
  baskets: BasketType[]
  orders: OrderType[]
  setBasketList: Dispatch<SetStateAction<BasketType[]>>
  setOrderList: Dispatch<SetStateAction<OrderType[]>>
  setStoreShippingList: Dispatch<SetStateAction<StoreShippingType[]>>
  storeShippings: StoreShippingType[]
  stores: StoreType[]
  users: UserType[]
}) => {
  const { baskets, orders, setBasketList, setOrderList, setStoreShippingList, storeShippings, stores, users } = params
  const dispatch = useDispatch()
  return useCallback(
    async (keyword: string) => {
      // キーワードが未入力であれば実行しない
      if (keyword === '') return
      // 日付範囲を選択して表示中の注文が存在する場合
      if (baskets.length > 0 || storeShippings.length > 0) {
        const filteredBaskets = baskets.filter((basket) => {
          const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
          const user = users.find((user) => user.user_id === basket.user_id)
          const store = stores.find((store) => store.store_id === basket.store_ref.id)
          // 受取番号か店名か注文者名で部分一致
          return (
            regexp.test(basket.basket_number) ||
            regexp.test(user ? user.name : '') ||
            isMatchedStoreName(keyword, store)
          )
        })
        setBasketList([...filteredBaskets])
        const filteredStoreShippings = storeShippings.filter((storeShipping) => {
          const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
          const user = users.find((user) => user.user_id === storeShipping.user_id)
          const store = stores.find((store) => store.store_id === storeShipping.store_ref.id)
          // 受取番号か店名か注文者名で部分一致
          return (
            regexp.test(storeShipping.shipping_number) ||
            regexp.test(user ? user.name : '') ||
            isMatchedStoreName(keyword, store)
          )
        })
        setStoreShippingList([...filteredStoreShippings])
        const filteredOrders = orders.filter(
          (order) =>
            filteredBaskets.some(
              (basket) => basket.user_id === order.user_id && ['daily', 'preorder'].includes(order.type)
            ) ||
            filteredStoreShippings.some(
              (storeShipping) => storeShipping.user_id === order.user_id && order.type === 'delivery'
            )
        )
        setOrderList([...filteredOrders])
      } else {
        // 表示中の注文が存在しない場合
        dispatch(showLoadingAction())
        const regexp = new RegExp('^(?=.*' + keyword + ').*$', 'g')
        let _baskets: BasketType[] = await searchBaskets(regexp)
        let _storeShippings: StoreShippingType[] = await searchStoreShippings(regexp)
        // 入力した受取番号が存在する場合はordersを取得する
        if (_baskets.length > 0 || _storeShippings.length > 0) {
          const ret = await Promise.all([fetchOrdersByBaskets(_baskets), fetchOrdersByStoreShippings(_storeShippings)])
          const _orders = ret.flat()
          setOrderList([..._orders])
        } else {
          // 店名または顧客名から検索する
          const _users = await searchUsers(regexp)
          const _stores = stores.filter((store) => isMatchedStoreName(keyword, store))

          // 顧客名と店名から受取番号を逆引きする
          const basketsByKeyword = await Promise.all([fetchBasketsByUsers(_users), fetchBasketsByStores(_stores)])
          _baskets = basketsByKeyword.flat().reduce((prev: BasketType[], basket: BasketType) => {
            // 100件以上は追加しない
            if (prev.length >= 100) {
              return prev
            } else if (prev.some((_basket) => _basket.basket_number === basket.basket_number)) {
              return prev
            } else {
              return [...prev, basket]
            }
          }, [])

          // 顧客名と店名から取り寄せ受注番号を逆引きする
          const storeShippingsByKeyword = await Promise.all([
            fetchStoreShippingsByUsers(_users),
            fetchStoreShippingsByStores(_stores),
          ])
          _storeShippings = storeShippingsByKeyword
            .flat()
            .reduce((prev: StoreShippingType[], storeShipping: StoreShippingType) => {
              // 100件以上は追加しない
              if (prev.length >= 100) {
                return prev
              } else if (
                prev.some((_storeShipping) => _storeShipping.shipping_number === storeShipping.shipping_number)
              ) {
                return prev
              } else {
                return [...prev, storeShipping]
              }
            }, [])

          const ret = await Promise.all([fetchOrdersByBaskets(_baskets), fetchOrdersByStoreShippings(_storeShippings)])
          const _orders = ret.flat()
          setOrderList([..._orders])
        }
        setBasketList([..._baskets])
        setStoreShippingList([..._storeShippings])
        dispatch(hideLoadingAction())
      }
    },
    [baskets, orders, setBasketList, setOrderList, setStoreShippingList, storeShippings, stores, users]
  )
}

export default useSearchOrder
